if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        
        if (this.elements.thumbnails) {
          this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector('button')
              .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          });
          if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        }
        
        // Auto-play functionality for product media gallery
        this.initAutoplay();
      }
      
      initAutoplay() {
        const slider = this.elements.viewer?.querySelector('[id^="Slider-"]');
        if (!slider) return;
        
        const autoplayEnabled = slider.getAttribute('data-autoplay') === 'true';
        if (!autoplayEnabled) return;
        
        const speed = parseInt(slider.getAttribute('data-speed')) || 5;
        this.autoplaySpeed = speed * 1000;
        this.autoplayInterval = null;
        this.isPaused = false;
        this.isAutoplayActive = false; // Flag to track if autoplay is switching slides
        
        // Pause on hover
        this.elements.viewer.addEventListener('mouseenter', () => {
          this.pauseAutoplay();
        });
        this.elements.viewer.addEventListener('mouseleave', () => {
          if (!this.isPaused) {
            this.startAutoplay();
          }
        });
        
        // Pause on user interaction
        const nextButton = this.elements.viewer.querySelector('button[name="next"]');
        const prevButton = this.elements.viewer.querySelector('button[name="previous"]');
        
        if (nextButton) {
          nextButton.addEventListener('click', () => {
            this.pauseAutoplay();
            setTimeout(() => {
              if (!this.isPaused) {
                this.startAutoplay();
              }
            }, 5000);
          });
        }
        
        if (prevButton) {
          prevButton.addEventListener('click', () => {
            this.pauseAutoplay();
            setTimeout(() => {
              if (!this.isPaused) {
                this.startAutoplay();
              }
            }, 5000);
          });
        }
        
        // Start autoplay
        this.startAutoplay();
      }
      
      startAutoplay() {
        if (this.autoplayInterval) return;
        
        const slider = this.elements.viewer?.querySelector('[id^="Slider-"]');
        if (!slider) return;
        
        this.autoplayInterval = setInterval(() => {
          // Get all slides
          const slides = slider.querySelectorAll('[id^="Slide-"]');
          if (slides.length <= 1) return;
          
          // Find current active slide
          const currentSlide = slider.querySelector('.is-active');
          if (!currentSlide) return;
          
          const currentIndex = Array.from(slides).indexOf(currentSlide);
          let nextIndex;
          
          // Determine next slide index
          if (currentIndex === slides.length - 1) {
            // Go back to first slide (loop)
            nextIndex = 0;
          } else {
            // Go to next slide
            nextIndex = currentIndex + 1;
          }
          
          // Get next slide and its media ID
          const nextSlide = slides[nextIndex];
          if (!nextSlide) return;
          
          const nextMediaId = nextSlide.getAttribute('data-media-id');
          if (nextMediaId) {
            // Set flag to indicate autoplay is active
            this.isAutoplayActive = true;
            // Use setActiveMedia to properly switch slides
            this.setActiveMedia(nextMediaId, false);
            // Reset flag after a short delay
            setTimeout(() => {
              this.isAutoplayActive = false;
            }, 100);
          }
        }, this.autoplaySpeed);
      }
      
      pauseAutoplay() {
        if (this.autoplayInterval) {
          clearInterval(this.autoplayInterval);
          this.autoplayInterval = null;
        }
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          // Don't scroll page if autoplay is active (prevents unwanted scroll on mobile)
          if (this.isAutoplayActive) return;
          
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
